import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck } from 'lucide-react';
import type { PermissionCatalogEntry, Role } from '@reflex/shared';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Switch } from '@reflex/ui/components/ui/switch';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { DetailPageShell } from '@/components/shared/DetailPageShell';
import { DirtyActionBar } from '@reflex/ui/components/shared/DirtyActionBar';
import { EntityDetailsCard } from '@/components/shared/EntityDetailsCard';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { NestedDetailHeader } from '@/components/shared/NestedDetailHeader';
import { useAuth } from '@/hooks/auth/useAuth';
import { useRefreshSession } from '@/hooks/auth/useSessionRefresh';
import { api } from '@/lib/api';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { mutateWithToast } from '@/lib/mutation-helpers';

/**
 * Display labels for the host's built-in permission categories. Plugin
 * contributors can introduce their own category strings — those fall
 * through to {@link toCategoryLabel} below, which Title-Cases the slug.
 */
const CATEGORY_LABELS: Record<string, string> = {
  org: 'Organization',
  agents: 'Agents',
  blueprints: 'Blueprints',
  mcp: 'MCP Servers',
  gateways: 'AI Gateways',
  'network-policies': 'Network Policies',
  secrets: 'Secrets',
  integrations: 'Integrations',
  flows: 'Automations',
  chat: 'Chat',
  personas: 'Personas',
  projects: 'Projects',
  themes: 'Themes',
  marketing: 'Marketing',
  admin: 'Administration',
};

function toCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * The catalog fields the picker renders. Narrower than
 * `PermissionCatalogEntry` on purpose: the picker only needs these three,
 * so it stays typed against them regardless of what enforcement metadata
 * (`mutates`, `defaultRoleGrants`) rides along in the payload.
 */
type PermissionListing = Pick<PermissionCatalogEntry, 'slug' | 'category' | 'description'>;

function groupByCategory(
  catalog: readonly PermissionListing[],
): ReadonlyArray<readonly [string, readonly PermissionListing[]]> {
  const groups = new Map<string, PermissionListing[]>();
  for (const entry of catalog) {
    const existing = groups.get(entry.category) ?? [];
    existing.push(entry);
    groups.set(entry.category, existing);
  }
  return Array.from(groups.entries());
}

export function OrganizationRoleDetailPage() {
  return (
    <RequireActiveOrganization title="Role">
      {(org) => <RoleDetailLoader orgId={org.id} />}
    </RequireActiveOrganization>
  );
}

function RoleDetailLoader({ orgId }: { orgId: string }) {
  const { roleId } = useParams<{ roleId: string }>();
  const id = roleId ?? '';
  const routes = useOrgRoutes();

  const roleQuery = useQuery({
    queryKey: ['organizations', orgId, 'roles', id],
    queryFn: () => api.getOrganizationRole(orgId, id),
    enabled: !!orgId && !!id,
  });

  if (!id) {
    return (
      <PageLayout>
        <PageHeader title="Role" description="Missing role id." />
      </PageLayout>
    );
  }

  return (
    <DetailPageShell
      title="Role"
      loadingDescription="Loading role details…"
      errorDescription="This role could not be loaded."
      back={{ to: routes.roles, label: 'Roles', ariaLabel: 'Back to roles' }}
      isLoading={roleQuery.isLoading}
      isError={roleQuery.isError}
      data={roleQuery.data}
    >
      {(role) => <RoleEditor key={role.id} role={role} orgId={orgId} />}
    </DetailPageShell>
  );
}

interface RoleEditorProps {
  role: Role;
  orgId: string;
}

function RoleEditor({ role, orgId }: RoleEditorProps) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const refreshSession = useRefreshSession();
  const canManage = can('roles:manage');
  const routes = useOrgRoutes();

  // Fetch the runtime catalog so plugin-contributed slugs (e.g.
  // `projects:read` from plugin-projects) appear when their plugin is
  // installed. Falls back to an empty list during the load; an empty
  // picker renders fine and reconciles on completion.
  const catalogQuery = useQuery({
    queryKey: ['permissions', 'catalog'],
    queryFn: () => api.getPermissionsCatalog(),
    staleTime: 5 * 60_000,
  });
  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const permissionsByCategory = useMemo(() => groupByCategory(catalog), [catalog]);

  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [permissions, setPermissions] = useState<Set<string>>(() => new Set(role.permissions));

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string | null; permissions?: string[] }) =>
      api.updateOrganizationRole(orgId, role.id, data),
    onSuccess: (next) => {
      queryClient.setQueryData(['organizations', orgId, 'roles', role.id], next);
      void queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'roles'] });
      refreshSession();
    },
  });

  const dirty = useMemo(() => {
    if (role.isBuiltin) return false;
    if (name !== role.name) return true;
    if ((description || null) !== (role.description ?? null)) return true;
    if (permissions.size !== role.permissions.length) return true;
    for (const p of role.permissions) {
      if (!permissions.has(p)) return true;
    }
    return false;
  }, [role, name, description, permissions]);

  function togglePermission(slug: string, next: boolean) {
    setPermissions((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(slug);
      else copy.delete(slug);
      return copy;
    });
  }

  function handleSave() {
    if (role.isBuiltin) return;
    const payload: { name?: string; description?: string | null; permissions?: string[] } = {};
    if (name !== role.name) payload.name = name;
    if ((description || null) !== (role.description ?? null)) {
      payload.description = description.length === 0 ? null : description;
    }
    const sortedPerms = Array.from(permissions).sort();
    const sortedRolePerms = [...role.permissions].sort();
    if (sortedPerms.join('|') !== sortedRolePerms.join('|')) {
      payload.permissions = sortedPerms;
    }
    if (Object.keys(payload).length === 0) return;
    mutateWithToast(updateMutation, payload, {
      success: 'Role updated',
      error: 'Failed to update role',
    });
  }

  function handleReset() {
    setName(role.name);
    setDescription(role.description ?? '');
    setPermissions(new Set(role.permissions));
  }

  const readOnly = !canManage;
  // Built-in roles are immutable at the API layer (see
  // `OrganizationService.updateRole`); the UI mirrors that contract by
  // disabling every editable surface and short-circuiting `dirty` /
  // `handleSave` above so no PATCH ever goes out.
  const lockAll = role.isBuiltin || readOnly;

  const permissionsAction = !lockAll ? (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setPermissions(new Set(catalog.map((p) => p.slug)))}
      >
        Select all
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setPermissions(new Set())}>
        Clear
      </Button>
    </div>
  ) : null;

  return (
    <PageLayout maxWidth="4xl">
      <NestedDetailHeader
        back={{ to: routes.roles, label: 'Roles', ariaLabel: 'Back to roles' }}
        title={role.name}
        description={role.description ?? 'Role details and permissions.'}
        docs="administration/organization-teams#roles-permissions"
        icon={<BadgeCheck className="h-5 w-5 text-muted-foreground" />}
        iconVariant="box"
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="role-detail-badges">
        <Badge variant="secondary">{role.scope === 'org' ? 'Organization' : 'Team'}</Badge>
        {role.isBuiltin ? (
          <Badge variant="outline">Built-in</Badge>
        ) : (
          <Badge variant="outline">Custom</Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {permissions.size} permission{permissions.size === 1 ? '' : 's'}
        </span>
      </div>

      <EntityDetailsCard
        description="Identifying information for this role."
        data-testid="role-details-section"
        disabled={lockAll}
        name={{
          id: 'role-name',
          label: 'Name',
          value: name,
          onChange: setName,
          maxLength: 100,
          hint: role.isBuiltin ? 'Built-in roles cannot be renamed.' : undefined,
        }}
        identifier={{
          id: 'role-slug',
          label: 'Identifier',
          value: role.slug,
          readOnly: true,
        }}
        descriptionField={{
          id: 'role-description',
          label: 'Description',
          value: description,
          onChange: setDescription,
          maxLength: 500,
        }}
        dirty={dirty}
        saving={updateMutation.isPending}
        onDiscard={handleReset}
        onSave={handleSave}
        hideFooter
      />

      <SectionCard
        title="Permissions"
        description="Allowed actions for users with this role."
        action={permissionsAction}
        data-testid="role-permissions-section"
      >
        {role.isBuiltin && (
          <PageDescription className="mb-4 mt-0">Built-in roles are read-only.</PageDescription>
        )}
        <div className="space-y-6">
          {permissionsByCategory.map(([category, entries]) => (
            <div key={category} className="space-y-2">
              <SectionLabel as="h3">{toCategoryLabel(category)}</SectionLabel>
              <ul className="divide-y divide-border rounded-md border">
                {entries.map((entry) => {
                  const checked = permissions.has(entry.slug);
                  const inputId = `perm-${entry.slug}`;
                  return (
                    <li
                      key={entry.slug}
                      className="flex items-center justify-between gap-4 p-3"
                      data-testid={`permission-row-${entry.slug}`}
                    >
                      <label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                            {entry.slug}
                          </code>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>
                      </label>
                      <Switch
                        id={inputId}
                        checked={checked}
                        onCheckedChange={(next) => togglePermission(entry.slug, next)}
                        disabled={lockAll}
                        aria-label={`Toggle ${entry.slug}`}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </SectionCard>

      <DirtyActionBar
        dirty={dirty}
        saving={updateMutation.isPending}
        onDiscard={handleReset}
        onSave={handleSave}
        permission="roles:manage"
      />
    </PageLayout>
  );
}
